if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        } else {
          /* D-1 / CL-1: `cart_type` is `page`, so there is no <cart-notification> and no
             <cart-drawer> to re-render - but the header's bag mark still has to be told. Ask for
             the one section that carries it; assets/svnty-cart-toast.js paints it off the answer. */
          formData.append('sections', 'cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        /* P28 lane E (cart P2-4): a 429 / 503 add is asked again once (svntyAddFetch, global.js); a refused answer reads as an error */
        (typeof svntyAddFetch === 'function' ? svntyAddFetch(`${routes.cart_add_url}`, config) : fetch(`${routes.cart_add_url}`, config).then((response) => response.json()))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              /* R3-24 (round 3): a Shopify error body can carry `message` without `description`.
                 `handleErrorMessage(errorMessage = false)`'s default parameter fires on
                 `undefined`, so that body used to hide the error line and skip the toast — a
                 silent failure on both the PDP and the card. Read both fields, then the house
                 string; never nothing. */
              this.handleErrorMessage(
                response.description || response.message || (window.svntyStrings || {}).add_error || ''
              );

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              /* P28 lane E: only the stock's own refusal (422) may say Sold out; a busy store (svntyAddFetch's 429 / 503 shape) never does */
              if (!soldOutMessage || response.status !== 422) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');

            /* ── THE IN-PLACE ADD (D-1 / CL-1, PARITY_GAPS 2026-09-19) ──────────────────────
               Trade's only answer when there is no in-page cart UI was
               `window.location = routes.cart_url`: every add - from the PDP, from a collection
               card, from inside the Choose-options modal - threw the shopper off the page she
               was standing on, lost the grid and the scroll, and fired no feedback at all.
               the sibling adds in place, so SVNTY does: we stay, assets/svnty-cart-toast.js has
               already repainted the header's bag mark off `response.sections` and raised the
               toast (the line's name, a View bag link, auto-dismiss) off the publish above.
               The navigation survives ONLY as the degradation when that layer is missing. */
            if (!this.cart) {
              if (!window.SVNTYToast) {
                window.location = window.routes.cart_url;
                return;
              }
              if (quickAddModal) quickAddModal.hide(true);
              return;
            }

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            /* A REFUSAL MUST BE VISIBLE NOW THAT WE STAY (D-1). While the add ended in a
               navigation, a throw here was survivable: the shopper landed on /cart and saw the
               truth. In place, a dropped connection or a throttled POST (Shopify answers 429 with
               an HTML body, so response.json() throws) would leave her looking at a button that
               simply stopped spinning. handleErrorMessage writes into the PDP's own error line, or
               raises the toast where a card has none. */
            this.handleErrorMessage((window.svntyStrings || {}).add_error || '');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        /* P20: the store's own sentences say "your cart"; this site's word is the bag */
        if (typeof errorMessage === 'string') errorMessage = errorMessage.replace(/\b(your|the) cart\b/g, '$1 bag');

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) {
          /* A collection card's quick-add form carries no error wrapper - before the in-place add
             it never needed one, because a refusal was followed by a navigation. Now the shopper
             stays, so the refusal has to reach her: the toast is the only surface the card has. */
          if (errorMessage && window.SVNTYToast) window.SVNTYToast.show(errorMessage);
          return;
        }
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
